import { useCallback, useState } from 'react'

import { readPanelPreference, writePanelPreference } from './panelPreference'

/**
 * The docked panel's state: open, width and the conversation it shows.
 *
 * A preference, not view state (plan §4.2): the panel is chrome on the
 * pathless `_app` layout, which may not carry a search schema (CLAUDE.md
 * §3.3), and it follows the person from page to page — so it lives in
 * `aiAnalyst.panel` in this browser, like theme and density. The linkable
 * view of a conversation is `/ai-analyst/c/$conversationId`.
 *
 * Held by the shell, which needs it in three places: the panel itself, the
 * page's reserved right padding, and the command menu's "Ask AI" action.
 */
export function useAnalystDock() {
  const [state, setState] = useState(readPanelPreference)

  const setOpen = useCallback((open: boolean) => {
    setState((current) => ({ ...current, open }))
    writePanelPreference({ open })
  }, [])

  const setConversationId = useCallback((conversationId: string | null) => {
    setState((current) => ({ ...current, conversationId }))
    writePanelPreference({ conversationId })
  }, [])

  // The panel saves the width itself once a drag ends; this only follows it.
  const setWidth = useCallback((width: number) => {
    setState((current) => ({ ...current, width }))
  }, [])

  return { ...state, setOpen, setConversationId, setWidth }
}

export type AnalystDock = ReturnType<typeof useAnalystDock>
